import { Menu, Transition } from "@headlessui/react";
import { LogoutIcon } from "@heroicons/react/outline";
import { CogIcon, PlusIcon, UsersIcon } from "@heroicons/react/solid";
import { isEmpty, values } from "lodash";
import { stringifyUrl } from "query-string";
import React, { Fragment } from "react";
import { matchPath, useHistory, useLocation } from "react-router-dom";
import { useAuthentication } from "../context/authentication";
import { generateReturnToUrl } from "../context/authentication/AuthenticationProvider";
import { useWorkspaces } from "../context/workspaces";
import classNames from "../utils/classNames";
import { avatarsApi } from "../utils/constants";
import LoadingIcon from "./icons/LoadingIcon";

interface Props {
  className?: string;
}

const MENU_ITEM_CLASSES =
  "flex items-center block py-2 px-4 text-sm text-gray-700 cursor-pointer";

const WorkspaceButton: React.FC<Props> = ({ className }) => {
  const {
    workspaces,
    currentWorkspaceId,
    setCurrentWorkspaceId,
    toggleCreateWorkspaceForm,
    currentWorkspace
  } = useWorkspaces();
  const { user, authenticationState, logout } = useAuthentication();
  const location = useLocation();
  const history = useHistory();

  const workspaceMatch = matchPath(location.pathname, {
    path: "/workspace"
  });

  const detailsPageMatch = matchPath<{
    section: string;
    subsection: string;
    id: string;
  }>(location.pathname, {
    path: ["/:section/:subsection/:id"]
  });

  if (authenticationState === "checking") {
    return (
      <LoadingIcon className="animate-spin -ml-1 mr-3 h-5 w-5 text-hotspotty-500 dark:text-white" />
    );
  }

  if (authenticationState === "unauthenticated") {
    return (
      <div className={classNames(className, "relative flex-shrink-0")}>
        <span
          onClick={() =>
            history.push(`/login?${generateReturnToUrl(location)}`)
          }
          className="bg-gray-50 cursor-pointer flex items-center text-sm text-gray-500 hover:text-gray-900 focus:outline-none dark:text-gray-200 dark:hover:text-white dark:bg-transparent"
        >
          Sign in
        </span>
      </div>
    );
  }

  return (
    <Menu as="div" className={classNames(className, "relative flex-shrink-0")}>
      {({ open }) => (
        <>
          <div>
            <Menu.Button className="bg-gray-50 rounded-full flex items-center text-sm text-gray-500 hover:text-gray-900 focus:outline-none dark:text-gray-200 dark:hover:text-white dark:bg-transparent">
              <>
                {user ? (
                  <img
                    className="order-last sm:order-first h-6 w-6 rounded-full ring-2 ring-gray-500 hover:ring-gray-900 dark:ring-gray-200 dark:hover:ring-white"
                    alt="User avatar"
                    src={
                      user.avatar_url ||
                      stringifyUrl({
                        url: `${avatarsApi}/initials/${user.name}.svg`,
                        query: { w: 24, h: 24 }
                      })
                    }
                    onError={(e: any) => {
                      e.target.onerror = null;
                      e.target.src = stringifyUrl({
                        url: `${avatarsApi}/initials/${user.name}.svg`,
                        query: { w: 24, h: 24 }
                      });
                    }}
                  />
                ) : (
                  <UsersIcon className="h-6 w-6" />
                )}
                <span className="mr-2 sm:mr-0 sm:ml-2 text-md font-medium">
                  {currentWorkspace ? (
                    currentWorkspace.name
                  ) : (
                    <span className="w-14 sm:w-20 h-3 rounded animate-pulse flex bg-gray-400" />
                  )}
                </span>
              </>
            </Menu.Button>
          </div>
          <Transition
            show={open}
            as={Fragment}
            enter="transition ease-out duration-100"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95"
          >
            <Menu.Items
              static
              className="origin-top-right absolute z-10 right-0 mt-2 w-48 rounded-md shadow-lg bg-white divide-y divide-gray-100 ring-1 ring-black ring-opacity-5 focus:outline-none dark:bg-gray-800 dark:divide-gray-600"
            >
              <div>
                <div className="px-4 pt-3 dark:text-gray-300 rounded-t-md">
                  <p className="text-xs">Signed in as</p>
                  <p className="text-sm font-medium">{user.name}</p>
                </div>

                <div className="py-1">
                  <Menu.Item>
                    {({ active }) => (
                      <div
                        onClick={() => logout()}
                        className={classNames(
                          active
                            ? "bg-gray-100 text-gray-900 dark:bg-gray-700 dark:text-white"
                            : "text-gray-700 dark:bg-gray-800 dark:text-white",
                          MENU_ITEM_CLASSES
                        )}
                      >
                        <LogoutIcon className="mr-1.5 h-4 w-4" />
                        <span>Sign out</span>
                      </div>
                    )}
                  </Menu.Item>
                </div>
              </div>

              {!isEmpty(workspaces) && (
                <>
                  <div className="px-4 py-2 dark:text-gray-300">
                    <p className="text-xs">Switch workspace</p>
                  </div>
                  <div className="py-1">
                    {values(workspaces).map(({ uuid, name }) => (
                      <Menu.Item key={uuid}>
                        {({ active }) => (
                          <div
                            onClick={() => {
                              setCurrentWorkspaceId(uuid);

                              if (
                                !workspaceMatch ||
                                (detailsPageMatch?.params.id &&
                                  detailsPageMatch.params.subsection !==
                                    "settings")
                              ) {
                                history.push("/workspace/dashboard");
                              }
                            }}
                            className={classNames(
                              active || currentWorkspaceId === uuid
                                ? "bg-gray-100 text-gray-900 dark:bg-gray-700 dark:text-white"
                                : "text-gray-700 dark:bg-gray-800 dark:text-white",
                              MENU_ITEM_CLASSES
                            )}
                          >
                            <span className="flex-1">{name}</span>
                            <CogIcon
                              className="ml-1.5 h-4 w-4 text-gray-500 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white"
                              onClick={(e) => {
                                e.stopPropagation();
                                setCurrentWorkspaceId(uuid);
                                history.push("/workspace/settings/general");
                              }}
                            />
                          </div>
                        )}
                      </Menu.Item>
                    ))}
                  </div>

                  <div className="py-1">
                    <Menu.Item>
                      {({ active }) => (
                        <button
                          type="button"
                          onClick={toggleCreateWorkspaceForm}
                          className={classNames(
                            "w-full flex items-center",
                            active
                              ? "bg-gray-100 text-gray-900 dark:bg-gray-700 dark:text-white"
                              : "text-gray-700 dark:bg-gray-800 dark:text-white",
                            MENU_ITEM_CLASSES
                          )}
                        >
                          <PlusIcon className="-ml-1 mr-1.5 h-4 w-4" />
                          <span>Create workspace</span>
                        </button>
                      )}
                    </Menu.Item>
                  </div>
                </>
              )}
            </Menu.Items>
          </Transition>
        </>
      )}
    </Menu>
  );
};

export default WorkspaceButton;
